import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  updateDoc
} from "firebase/firestore";
import {
  getAuth,
  signInAnonymously,
  setPersistence,
  browserLocalPersistence
} from "firebase/auth";
import Cookies from "js-cookie";
import { Randomcode } from "./components_root/rendom_code";

/* ============================
   🔹 Firebase Config
============================ */
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

/* ============================
   🔹 Init Firebase
============================ */
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app); 


/* ============================
   🔐 Auth Persistence (MANDATORY)
============================ */
setPersistence(auth, browserLocalPersistence).catch(console.error);

/* ============================
   🔐 Safe Anonymous Auth (LOCKED)
============================ */
let authPromise = null;

const ensureFirebaseAuth = async () => {
  if (auth.currentUser) return;

  if (!authPromise) {
    authPromise = signInAnonymously(auth)
      .then(() => {
        console.log("✅ Firebase anonymous UID:", auth.currentUser.uid);
      })
      .catch(err => {
        authPromise = null;
        throw err;
      });
  }

  await authPromise;
};

/* ============================
   📱 MOBILE + OTP LOGIN
============================ */
export const Mobile_signIn = async (mobile, otp) => {
  try {
    if (!mobile || !otp) {
      return { success: false, error: "Mobile or OTP missing" };
    }

    // 🔐 Ensure Firebase Auth ONCE
    await ensureFirebaseAuth();

    if (!auth.currentUser) {
      throw new Error("Firebase authentication failed");
    }

    // 🔍 Verify OTP
    const checkQuery = query(
      collection(db, "accounts"),
      where("mobile", "==", mobile),
      where("otp", "==", otp)
    );

    const snap = await getDocs(checkQuery);

    if (snap.empty) {
      return { success: false, error: "Invalid OTP" };
    }

    const userData = snap.docs[0].data();
    const account_guid = userData.guid;

    // 🔄 Update account
    await updateDoc(doc(db, "accounts", account_guid), {
      updated_datetime: new Date(),
    });

    // 🔐 Create session
    const uid = Randomcode(12);

    await setDoc(doc(db, "accounts_login", uid), {
      guid: account_guid,
      uid,
      sessiontoken: uid,
      name: userData.name || "",
      mobile,
      device: navigator.userAgent,
      status: true,
      date_time: new Date(),
    });

    go_login(uid, account_guid);
    return { success: true };

  } catch (error) {
    console.error("❌ Mobile Sign-In Error:", error);

    if (error.code === "auth/too-many-requests") {
      return {
        success: false,
        error: "Too many attempts. Please wait 1 minute and try again."
      };
    }

    return { success: false, error: error.message || "Login failed" };
  }
};

/* ============================
   🍪 COOKIE + SESSION LOGIN
============================ */

const go_login = (uid, account_guid) => {

  const hostname = window.location.hostname;
  const isHttps = window.location.protocol === "https:";
  const isLocalhost =
    hostname === "localhost" || hostname === "127.0.0.1";

  const cookieOptions = {
    expires: 1,
    path: "/",
    ...(!isLocalhost && isHttps && { domain: ".aooz.in" }),
    secure: isHttps,
    sameSite: isHttps ? "None" : "Lax",
  };

  Cookies.remove("sessiontoken", { path: "/" });
  Cookies.remove("guid", { path: "/" });

  Cookies.set("sessiontoken", uid, cookieOptions);
  Cookies.set("guid", account_guid, cookieOptions);

  window.location.href = "/";
};
 
/* ============================
   🚪 LOGOUT
============================ */
export const googleLogout = () => {
  const hostname = window.location.hostname;

  // Generate possible domains
  const domains = [
    hostname,
    `.${hostname}`,
    hostname.split(".").slice(-2).join("."), // example.com
    `.${hostname.split(".").slice(-2).join(".")}`, // .example.com
  ];

  const cookieNames = [
    "sessiontoken",
    "guid",
    "token",
    "accessToken",
    "refreshToken",
  ];

  cookieNames.forEach((cookie) => {
    // Remove without domain
    Cookies.remove(cookie, { path: "/" });

    // Remove from possible domains
    domains.forEach((domain) => {
      Cookies.remove(cookie, {
        path: "/",
        domain,
      });
    });
  });

  // Clear localStorage
  localStorage.clear();

  // Clear sessionStorage
  sessionStorage.clear();

  // Redirect
  window.location.replace("/login");
}; 

export { auth, db};
